import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {IntersectionObserver | null} */
  #upsellObserver = null;

  /** @type {AbortController | null} */
  #cartUpsellAbortController = null;

  /** @type {MutationObserver | null} */
  #cartMutationObserver = null;

  /** @type {any} */
  #cartUpsellSwiper = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.addEventListener('load', this.#handleWindowLoad);
    this.#cartMutationObserver = new MutationObserver(() => this.#setupCartUpsellRecommendations());
    this.#cartMutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-url', 'data-recommendations-loaded'],
    });
    this.#setupCartUpsellRecommendations();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#upsellObserver?.disconnect();
    this.#cartUpsellAbortController?.abort();
    this.#cartMutationObserver?.disconnect();
    this.#cartUpsellSwiper?.destroy?.(true, true);
    window.removeEventListener('load', this.#handleWindowLoad);
  }

  /**
   * Compatibility entry point used by Kite after it changes free gift lines.
   * Refresh the whole drawer through theme events, including the empty state.
   */
  async open() {
    const cartItems =
      /** @type {import('./component-cart-items.js').CartItemsComponent | null} */ (
        this.querySelector('cart-items-component')
      );
    if (
      !cartItems?.dataset.sectionId ||
      typeof cartItems.fetchCartData !== 'function'
    )
      return;

    const sectionId = cartItems.dataset.sectionId;
    const previousKeys = [...cartItems.querySelectorAll('tr[data-key]')].map(
      (row) => row.getAttribute('data-key'),
    );

    try {
      const [cart, sectionHTML] = await Promise.all([
        cartItems.fetchCartData(),
        sectionRenderer.getSectionHTML(sectionId, false),
      ]);
      const currentKeys = new Set(cart.items.map((item) => item.key));
      const removedLines = previousKeys
        .filter((key) => key && !currentKeys.has(key))
        .map((id) => ({ id, quantity: 0 }));
      const lines = removedLines.length
        ? removedLines
        : cart.items.map((item) => ({ id: item.key, quantity: item.quantity }));

      if (lines.length) {
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: removedLines.length ? 'remove' : 'update',
            context: 'cart',
            lines,
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                sections: { [sectionId]: sectionHTML },
                items: cart.items,
                itemCount: cart.item_count,
                source: 'kite-cart-refresh',
                didError: false,
              },
            }),
          }),
        );
      } else {
        morphSection(sectionId, sectionHTML, {
          mode: 'hydration',
          injectStylesheet: true,
        });
      }
      this.#themeDrawer?.open();
    } catch (error) {
      console.warn(
        '[cart-drawer] Unable to refresh after Kite cart update:',
        error,
      );
    }
  }

  #handleWindowLoad = () => this.#setupCartUpsellSwiper();

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#setupCartUpsellRecommendations();
    this.#setupUpsellJump();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#setupCartUpsellRecommendations();
            this.#setupUpsellJump();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Loads a Complete Your Set card from Shopify Product Recommendations.
   * Complementary recommendations are preferred, with related products as a fallback.
   */
  async #setupCartUpsellRecommendations() {
    const container = this.#dialog?.querySelector('[data-bcs-cart-upsell-recommendations]');
    if (!(container instanceof HTMLElement)) return;

    if (container.dataset.recommendationsLoaded === 'true') {
      this.#setupCartUpsellSwiper();
      this.#setupUpsellJump();
      return;
    }
    if (container.dataset.recommendationsLoading === 'true') return;

    const urls = [container.dataset.url, container.dataset.fallbackUrl].filter(Boolean);
    if (!urls.length) return;

    this.#cartUpsellAbortController?.abort();
    const abortController = new AbortController();
    this.#cartUpsellAbortController = abortController;
    container.dataset.recommendationsLoading = 'true';

    try {
      for (const url of urls) {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) continue;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.querySelector('[data-bcs-cart-upsell-recommendations]');
        if (!(incoming instanceof HTMLElement) || !incoming.querySelector('#BcsCartUpsell')) continue;

        container.innerHTML = incoming.innerHTML;
        container.dataset.recommendationsLoaded = 'true';
        container.removeAttribute('hidden');
        this.#setupCartUpsellSwiper();
        this.#setupUpsellJump();
        return;
      }

      container.hidden = true;
      const jump = this.#dialog?.querySelector('.bcs-cart-upsell-jump');
      if (jump instanceof HTMLElement) jump.hidden = true;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[bcs-cart-upsell-recommendations] Could not load recommendations.', error);
      }
    } finally {
      delete container.dataset.recommendationsLoading;
      if (this.#cartUpsellAbortController === abortController) this.#cartUpsellAbortController = null;
    }
  }

  /** Initializes the cart upsell using the same one-card Swiper behavior as Complete Your Set. */
  #setupCartUpsellSwiper() {
    const slider = this.#dialog?.querySelector('[data-bcs-cart-upsell-swiper]');
    if (!(slider instanceof HTMLElement)) return;

    const slides = slider.querySelectorAll('.bcs-cart-upsell__slide');
    if (slides.length < 2 || typeof window.Swiper !== 'function') return;

    if (this.#cartUpsellSwiper?.el === slider) {
      this.#cartUpsellSwiper.update?.();
      return;
    }

    this.#cartUpsellSwiper?.destroy?.(true, true);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#cartUpsellSwiper = new window.Swiper(slider, {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: reduceMotion ? 0 : 420,
      autoHeight: true,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      touchStartPreventDefault: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: this.#dialog?.querySelector('[data-bcs-cart-upsell-prev]'),
        nextEl: this.#dialog?.querySelector('[data-bcs-cart-upsell-next]'),
      },
    });
  }

  /**
   * Shows the compact upsell jump only while the Complete Your Set section is
   * outside the cart body's visible scroll area.
   */
  #setupUpsellJump() {
    this.#upsellObserver?.disconnect();
    this.#upsellObserver = null;

    const dialog = this.#dialog;
    const scrollArea = dialog?.querySelector('.cart-drawer__items');
    const upsell = dialog?.querySelector('#BcsCartUpsell');
    const jump = dialog?.querySelector('.bcs-cart-upsell-jump');

    if (!(scrollArea instanceof Element) || !(upsell instanceof Element) || !(jump instanceof HTMLElement)) return;

    this.#upsellObserver = new IntersectionObserver(
      ([entry]) => {
        jump.hidden = Boolean(entry?.isIntersecting);
      },
      {
        root: scrollArea,
        threshold: 0.01,
      }
    );
    this.#upsellObserver.observe(upsell);
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
